import { createHash } from "node:crypto";
import { minimatch } from "minimatch";
import { isSensitiveFile } from "./credentials.js";
import { isBinaryFile } from "./shared/binary.js";
import { isGenerationBoundary, isGenerationCommit, isRevertCommit, parseRevertedMessage, parseRevertedSha } from "./git/CommitDetection.js";
import type { GitClient } from "./git/GitClient.js";
import type { LockfileManager } from "./LockfileManager.js";
import type { CommitInfo, DegradedReason, GenerationLock, GenerationRecord, StoredPatch } from "./types.js";

// Infrastructure files managed by the generation pipeline, not user code.
// Changes to these should never be captured as customization patches.
const INFRASTRUCTURE_FILES = new Set([".fernignore"]);

function matchesFernignorePattern(filePath: string, patterns: readonly string[]): boolean {
    if (patterns.length === 0) return false;
    return patterns.some((rawPattern) => {
        // Gitignore directory syntax: `docs/` means the directory and
        // everything underneath it. Strip the trailing slash so the exact,
        // glob, and bare-directory checks below all apply.
        const p = rawPattern.endsWith("/") ? rawPattern.slice(0, -1) : rawPattern;
        if (filePath === p) return true;
        if (minimatch(filePath, p)) return true;
        // Gitignore-style: a bare directory name (no wildcard) also
        // matches all paths underneath it. Customers commonly write
        // `api-report` (not `api-report/**`) and expect children to be
        // protected. This expansion mirrors fern's wipe semantics.
        if (!p.includes("*") && !p.includes("?") && filePath.startsWith(p + "/")) return true;
        return false;
    });
}

/**
 * Parse the `diff --git a/<p> b/<p>` headers out of a `git format-patch` blob
 * and classify each file as a creation (`new file mode …`), deletion
 * (`deleted file mode …`), or neither (modify / rename / chmod).
 *
 * Only handles the unquoted `a/…` `b/…` form — git quotes paths that contain
 * control characters or non-UTF-8 bytes (`"a/weird\tname.txt"`), which the
 * parser skips. SDK files don't contain such characters in practice; if that
 * assumption is ever violated, quoted headers yield `currentPath = null` and
 * are silently ignored (false negative, never a false positive).
 */
function parsePatchFileHeaders(patchContent: string): Array<{ path: string; isCreate: boolean; isDelete: boolean }> {
    const results: Array<{ path: string; isCreate: boolean; isDelete: boolean }> = [];
    let currentPath: string | null = null;
    let currentIsCreate = false;
    let currentIsDelete = false;

    const flush = () => {
        if (currentPath !== null) {
            results.push({ path: currentPath, isCreate: currentIsCreate, isDelete: currentIsDelete });
        }
    };

    for (const line of patchContent.split("\n")) {
        if (line.startsWith("diff --git ")) {
            flush();
            currentPath = null;
            currentIsCreate = false;
            currentIsDelete = false;
            // Unquoted form: "diff --git a/<path> b/<path>". Non-greedy matching
            // handles paths with spaces (e.g. "sdk files/helper.py") in the common
            // case. Pathological paths containing the literal substring " b/" are
            // mis-parsed; see the docstring for the safety argument (false
            // negatives are harmless, false positives are impossible).
            const match = line.match(/^diff --git a\/(.+?) b\/(.+?)$/);
            if (match) {
                // Track both sides — rename commits have a != b. Creations and
                // deletions always have a == b, so we record the (post-rename)
                // "b" path which is what `patch.files` will contain.
                currentPath = match[2] ?? null;
            }
        } else if (currentPath !== null) {
            if (line.startsWith("new file mode ")) {
                currentIsCreate = true;
            } else if (line.startsWith("deleted file mode ")) {
                currentIsDelete = true;
            }
        }
    }
    flush();
    return results;
}

async function capturePatchSnapshot(
    git: GitClient,
    files: string[],
    treeish: string,
): Promise<Record<string, string>> {
    const snapshot: Record<string, string> = {};
    for (const file of files) {
        if (isBinaryFile(file)) continue;
        const content = await git.showFile(treeish, file).catch(() => null);
        if (content != null) snapshot[file] = content;
    }
    return snapshot;
}

function filterInfrastructureAndSensitiveFiles(
    rawFilesOutput: string,
    fernignorePatterns: readonly string[],
): { files: string[]; sensitiveFiles: string[]; fernignoredFiles: string[]; infrastructureFiles: string[] } {
    const rawFiles = rawFilesOutput.trim().split("\n").filter(Boolean);
    const infrastructureFiles = rawFiles.filter((f) => INFRASTRUCTURE_FILES.has(f) || f.startsWith(".fern/"));
    const allFiles = rawFiles.filter((f) => !INFRASTRUCTURE_FILES.has(f) && !f.startsWith(".fern/"));
    const sensitiveFiles = allFiles.filter((f) => isSensitiveFile(f));
    const nonSensitive = allFiles.filter((f) => !isSensitiveFile(f));
    // ADR 0002: `.fernignore`-matched files are invisible to Replay. They
    // never enter `patch.files`, `patch_content`, or `theirs_snapshot`.
    const fernignoredFiles = nonSensitive.filter((f) => matchesFernignorePattern(f, fernignorePatterns));
    const files = nonSensitive.filter((f) => !matchesFernignorePattern(f, fernignorePatterns));
    return { files, sensitiveFiles, fernignoredFiles, infrastructureFiles };
}

export interface DetectionResult {
    patches: StoredPatch[];
    revertedPatchIds: string[];
}

/**
 * Result of materializing a commit into patch content through the exact
 * pipeline detection uses (file filtering → scoped diff or format-patch →
 * content hash). `sensitiveFiles` / `fernignoredFiles` are surfaced so
 * callers can emit the same customer-facing warnings detection does.
 *
 * - `ok` — patch content and its content hash were produced.
 * - `unreachable` — the commit (or its patch text) could not be read, e.g.
 *   a shallow clone window or a GC'd object.
 * - `empty` — nothing survives filtering (only infrastructure / sensitive /
 *   fernignored files, or the scoped diff is empty).
 */
export type MaterializedCommitPatch =
    | {
          status: "ok";
          files: string[];
          sensitiveFiles: string[];
          fernignoredFiles: string[];
          patchContent: string;
          contentHash: string;
      }
    | { status: "unreachable"; sensitiveFiles: string[]; fernignoredFiles: string[] }
    | { status: "empty"; sensitiveFiles: string[]; fernignoredFiles: string[] };

export class ReplayDetector {
    private git: GitClient;
    private lockManager: LockfileManager;
    private sdkOutputDir: string;
    private fernignorePatterns: readonly string[];
    readonly warnings: string[] = [];
    /**
     * Structured degraded-state channel, mirroring `warnings`. Populated when
     * detection cannot guarantee customizations were carried forward (baseline
     * unreachable in this clone). Cleared per-run by `ReplayService.prepareReplay`
     * alongside `warnings`. Merged into `ReplayReport.degradedReasons` by each
     * flow handler. Degraded is observability only: detection results are
     * unchanged.
     */
    readonly degradedReasons: DegradedReason[] = [];

    constructor(
        git: GitClient,
        lockManager: LockfileManager,
        sdkOutputDir: string,
        fernignorePatterns: readonly string[] = [],
    ) {
        this.git = git;
        this.lockManager = lockManager;
        this.sdkOutputDir = sdkOutputDir;
        this.fernignorePatterns = fernignorePatterns;
    }

    /**
     * Derive the previous-generation SHA from git history instead of trusting
     * `lock.current_generation`.
     *
     * Walks `git log HEAD --first-parent` and returns the SHA of the most recent
     * commit that `isGenerationBoundary` classifies as a customization-baseline
     * boundary, or null if no such commit is reachable from HEAD on the mainline.
     *
     * `isGenerationBoundary` is intentionally narrower than `isGenerationCommit`:
     * it excludes `[fern-autoversion]` (lightweight bumps that don't reset the
     * baseline) and the GitHub merge-commit subject for fern-bot regen PRs
     * (which sits on first-parent but where the customer fix-up commits live
     * on second-parent — stopping at the merge would hide them).
     *
     * See docs/adr/0001-derived-scan-boundary.md.
     */
    async findPreviousGenerationFromHistory(): Promise<string | null> {
        let log: string;
        try {
            log = await this.git.exec([
                "log",
                "--first-parent",
                "--format=%H%x00%an%x00%ae%x00%s",
                "HEAD",
            ]);
        } catch {
            return null;
        }

        for (const commit of this.parseGitLog(log)) {
            // Use the narrower `isGenerationBoundary` predicate — autoversion
            // commits skip the detector but DON'T reset the customization
            // baseline, so they must not be treated as the scan boundary.
            if (isGenerationBoundary(commit)) {
                return commit.sha;
            }
        }

        // Fallback: the first-parent walk found no boundary. This happens when the only
        // [fern-generated] commit lives on a merge's SECOND parent (e.g. a regen PR merged
        // via "Create a merge commit"). `--first-parent` never visits it, so walk the full
        // graph (excluding merge commits themselves) before giving up — otherwise the
        // boundary is invisible and the customer's customizations are silently dropped.
        // Safety: only reached when first-parent found nothing, so we cannot pick a
        // boundary on an unrelated branch ahead of the mainline one.
        let fullLog: string;
        try {
            fullLog = await this.git.exec([
                "log",
                "--no-merges",
                "--format=%H%x00%an%x00%ae%x00%s",
                "HEAD",
            ]);
        } catch {
            return null;
        }
        for (const commit of this.parseGitLog(fullLog)) {
            if (isGenerationBoundary(commit)) {
                return commit.sha;
            }
        }
        return null;
    }

    async detectNewPatches(): Promise<DetectionResult> {
        const lock = this.lockManager.read();

        // Prefer the previous-generation SHA derived from git history. This
        // self-heals when `lock.current_generation` is stale (orphaned by a
        // squash-merge, GC'd, or pointing at an older generation than what's
        // actually reachable from HEAD). See docs/adr/0001-derived-scan-boundary.md.
        const derivedSha = await this.findPreviousGenerationFromHistory();
        if (derivedSha !== null) {
            const derivedRecord = await this.synthesizeGenerationRecord(derivedSha);
            return this.detectPatchesInRange(derivedSha, derivedRecord, lock);
        }

        const lastGen = this.getLastGeneration(lock);
        if (!lastGen) {
            // Guard against silent 0-patch loss. When `current_generation` is set, the
            // lockfile was tracking a prior generation that is now unresolvable (no
            // boundary in history AND no matching record in `lock.generations`). We
            // cannot recover patches without a baseline, but staying silent would let
            // the next regeneration drop the customer's customizations unnoticed.
            if (lock.current_generation) {
                this.warnings.push(
                    `No generation boundary found in git history and the lockfile's recorded ` +
                    `generation (${lock.current_generation.slice(0, 7)}) matches no entry in its ` +
                    `generation records. Customer customizations may differ from the last known ` +
                    `generation and could be lost on the next regeneration. Run ` +
                    `\`fern-replay bootstrap --force\` to re-initialize tracking.`
                );
                // Structured channel: no baseline at all = this run cannot
                // verify customizations were carried forward.
                this.degradedReasons.push({
                    code: "baseline-unresolvable",
                    message:
                        `The previous generation recorded in the lockfile ` +
                        `(${lock.current_generation.slice(0, 7)}) could not be resolved to any ` +
                        `reachable baseline during this run. Customizations may not have been ` +
                        `carried forward — review the result before merging.`,
                });
            }
            return { patches: [], revertedPatchIds: [] };
        }

        // Lockfile-driven fall-through. Two cases reach here:
        //  - Shallow clone where the generation commit isn't in the clone
        //    window. `commitExists` returns false; `detectPatchesViaTreeDiff`
        //    recovers from the recorded `tree_hash` (commit-free).
        //  - Pathological topology where the recorded generation is reachable
        //    in the object database but not on HEAD's first-parent line.
        //    Rare; we route through tree-diff for safety.
        //
        // The pre-FER-10498 `detectPatchesViaMergeBase` recovery is gone —
        // first-parent walking in `findPreviousGenerationFromHistory` handles
        // squash-merge orphaning directly.
        const exists = await this.git.commitExists(lastGen.commit_sha);
        if (!exists) {
            this.warnings.push(
                `Generation commit ${lastGen.commit_sha.slice(0, 7)} not found in git history. ` +
                    `Falling back to tree-diff detection (likely a shallow clone).`
            );
            // Structured channel: when tracked patches exist, an unreachable
            // anchor means they cannot be verified against their baseline.
            // With zero tracked patches, tree-diff recovery against a
            // reachable tree is recovery, not degradation — warning only.
            if (lock.patches.length > 0) {
                this.degradedReasons.push({
                    code: "generation-anchor-unreachable",
                    message:
                        `The previous generation (${lastGen.commit_sha.slice(0, 7)}) was not ` +
                        `reachable during this run, and ${lock.patches.length} tracked ` +
                        `customization(s) could not be verified against it. Customizations may ` +
                        `not have been carried forward — review the result before merging.`,
                });
            }
            return this.detectPatchesViaTreeDiff(lastGen, /* commitKnownMissing */ true);
        }

        return this.detectPatchesInRange(lastGen.commit_sha, lastGen, lock);
    }

    /**
     * Walk `${rangeStart}..HEAD`, classify each commit, emit one
     * `StoredPatch` per customer commit. Body shared by the linear path
     * (rangeStart = lastGen.commit_sha) and the merge-base path.
     *
     * Patch `base_generation` is always `lastGen.commit_sha` — the
     * lockfile-tracked reference the applicator uses to fetch base file
     * content, regardless of where the walk actually started.
     */
    private async detectPatchesInRange(
        rangeStart: string,
        lastGen: GenerationRecord,
        lock: GenerationLock,
    ): Promise<DetectionResult> {
        // `--no-merges` walks every non-merge commit reachable in the range,
        // including ones that arrived via a merged-in branch (e.g. a regen PR
        // merged with "Create a merge commit"). The prior `--first-parent`
        // would skip those silently, dropping customer fix-up commits.
        // Merge-commit-only edits ("evil merges") are a known limitation.
        const log = await this.git.exec([
            "log",
            "--no-merges",
            "--format=%H%x00%an%x00%ae%x00%s",
            `${rangeStart}..HEAD`,
            "--",
            this.sdkOutputDir
        ]);

        if (!log.trim()) {
            return { patches: [], revertedPatchIds: [] };
        }

        const commits = this.parseGitLog(log);
        const newPatches: StoredPatch[] = [];
        const forgottenHashes = new Set(lock.forgotten_hashes ?? []);

        for (const commit of commits) {
            if (isGenerationCommit(commit)) {
                continue;
            }

            if (lock.patches.find((p) => p.original_commit === commit.sha)) {
                continue;
            }

            const materialized = await this.materializeCommitPatch(commit.sha);

            if (materialized.sensitiveFiles.length > 0) {
                this.warnings.push(
                    `Sensitive file(s) excluded from patch for commit ${commit.sha.slice(0, 7)}: ` +
                    `${materialized.sensitiveFiles.join(", ")}. These files will not be tracked by replay.`
                );
            }

            if (materialized.fernignoredFiles.length > 0) {
                this.warnings.push(
                    `.fernignore-protected file(s) excluded from patch for commit ${commit.sha.slice(0, 7)}: ` +
                    `${materialized.fernignoredFiles.join(", ")}. These files are managed by .fernignore, not Replay.`
                );
            }

            if (materialized.status === "unreachable") {
                this.warnings.push(
                    `Could not generate patch for commit ${commit.sha.slice(0, 7)} — ` +
                    `it may be unreachable in a shallow clone. Skipping.`
                );
                continue;
            }

            // Skip patches that only touch infrastructure/sensitive/fernignored files.
            if (materialized.status === "empty") {
                continue;
            }

            const { files, patchContent, contentHash } = materialized;
            if (lock.patches.find((p) => p.content_hash === contentHash) || forgottenHashes.has(contentHash)) {
                continue;
            }

            // Snapshot THEIRS from the customer's commit tree (post-edit content).
            const theirsSnapshot = await capturePatchSnapshot(this.git, files, commit.sha);

            newPatches.push({
                id: `patch-${commit.sha.slice(0, 8)}`,
                content_hash: contentHash,
                original_commit: commit.sha,
                original_message: commit.message,
                original_author: `${commit.authorName} <${commit.authorEmail}>`,
                base_generation: lastGen.commit_sha,
                files,
                patch_content: patchContent,
                ...(Object.keys(theirsSnapshot).length > 0 ? { theirs_snapshot: theirsSnapshot } : {}),
                ...(this.isCreationOnlyPatch(patchContent) ? { user_owned: true } : {})
            });
        }

        // Collapse zero-net-change files. Each commit becomes its own
        // patch, so a file created in commit A and deleted in commit B
        // survives as two patches whose cumulative diff is empty. This
        // filter drops such patches before they reach the lockfile — and
        // importantly, this also prevents committed-then-deleted
        // credentials (.env, .pem) from leaking their contents verbatim
        // into replay.lock.
        const survivingPatches = await this.collapseNetZeroFiles(newPatches);

        // Reverse so patches apply in chronological order (oldest first)
        survivingPatches.reverse();

        // Reconcile revert commits: match against existing and newly-detected patches.
        // Uses a Set for revertedPatchIds to avoid duplicate counting when multiple
        // reverts target the same patch.
        const revertedPatchIdSet = new Set<string>();
        const revertIndicesToRemove = new Set<number>();

        for (let i = 0; i < survivingPatches.length; i++) {
            const patch = survivingPatches[i]!;
            if (!isRevertCommit(patch.original_message)) continue;

            // Get full commit body for SHA extraction. May fail in shallow clones
            // or if the commit was GC'd — fall back to message-only matching.
            let body = "";
            try {
                body = await this.git.getCommitBody(patch.original_commit);
            } catch {
                // Shallow clone or unreachable commit — message matching only
            }
            const revertedSha = parseRevertedSha(body);
            const revertedMessage = parseRevertedMessage(patch.original_message);

            // Try to match against existing lockfile patches
            let matchedExisting = false;
            if (revertedSha) {
                const existing = lock.patches.find((p) => p.original_commit === revertedSha);
                if (existing) {
                    revertedPatchIdSet.add(existing.id);
                    revertIndicesToRemove.add(i);
                    matchedExisting = true;
                }
            }
            if (!matchedExisting && revertedMessage) {
                const existing = lock.patches.find((p) => p.original_message === revertedMessage);
                if (existing) {
                    revertedPatchIdSet.add(existing.id);
                    revertIndicesToRemove.add(i);
                    matchedExisting = true;
                }
            }

            if (matchedExisting) continue;

            // Try to match against newly-detected patches in the same run
            let matchedNew = false;
            if (revertedSha) {
                const idx = survivingPatches.findIndex(
                    (p, j) => j !== i && !revertIndicesToRemove.has(j) && p.original_commit === revertedSha
                );
                if (idx !== -1) {
                    revertIndicesToRemove.add(i);
                    revertIndicesToRemove.add(idx);
                    matchedNew = true;
                }
            }
            if (!matchedNew && revertedMessage) {
                const idx = survivingPatches.findIndex(
                    (p, j) => j !== i && !revertIndicesToRemove.has(j) && p.original_message === revertedMessage
                );
                if (idx !== -1) {
                    revertIndicesToRemove.add(i);
                    revertIndicesToRemove.add(idx);
                }
            }

            // Unmatched revert: the original commit predates replay (e.g. .fernignore era).
            // A revert is never a customization — drop it so it doesn't become a patch.
            if (!matchedExisting && !matchedNew) {
                revertIndicesToRemove.add(i);
            }
        }

        const filteredPatches = survivingPatches.filter((_, i) => !revertIndicesToRemove.has(i));
        return { patches: filteredPatches, revertedPatchIds: [...revertedPatchIdSet] };
    }

    /**
     * Materialize a commit into patch content exactly as detection does.
     *
     * Pipeline: `diff-tree --name-only` → filter infrastructure / sensitive /
     * `.fernignore`d files → scoped `git diff` when files were stripped,
     * otherwise full `git format-patch` → content hash.
     *
     * This is THE definition of a commit's re-emission identity: the content
     * hash this method returns is what `detectPatchesInRange` will compute if
     * the commit re-enters the scan range (e.g. after a regen PR is merged
     * with a merge commit). Callers that tombstone a dismissed patch must use
     * this hash — the stored `content_hash` drifts every time the patch is
     * carried forward, so it does not identify the commit on re-detection.
     *
     * Never throws; failures are reported through the `status` discriminant.
     */
    async materializeCommitPatch(sha: string): Promise<MaterializedCommitPatch> {
        let parents: string[];
        let filesOutput: string;
        try {
            parents = await this.git.getCommitParents(sha);
            filesOutput = await this.git.exec(["diff-tree", "--no-commit-id", "--name-only", "-r", sha]);
        } catch {
            return { status: "unreachable", sensitiveFiles: [], fernignoredFiles: [] };
        }

        const { files, sensitiveFiles, fernignoredFiles, infrastructureFiles } =
            filterInfrastructureAndSensitiveFiles(filesOutput, this.fernignorePatterns);

        // Nothing survives filtering — only infrastructure/sensitive/fernignored files.
        if (files.length === 0) {
            return { status: "empty", sensitiveFiles, fernignoredFiles };
        }

        // When ANY files were stripped (infrastructure, sensitive, or
        // fernignored), use a diff scoped to the surviving files so
        // `patch_content` matches `files`. This prevents infrastructure file
        // changes (e.g. .fernignore edits) from being bundled into patches and
        // silently re-applied on every regeneration.
        const wasFiltered =
            sensitiveFiles.length > 0 || fernignoredFiles.length > 0 || infrastructureFiles.length > 0;
        const parentSha = parents[0];
        let patchContent: string;
        if (wasFiltered && parentSha) {
            try {
                patchContent = await this.git.exec(["diff", parentSha, sha, "--", ...files]);
            } catch {
                return { status: "empty", sensitiveFiles, fernignoredFiles };
            }
            if (!patchContent.trim()) {
                return { status: "empty", sensitiveFiles, fernignoredFiles };
            }
        } else {
            try {
                patchContent = await this.git.formatPatch(sha);
            } catch {
                return { status: "unreachable", sensitiveFiles, fernignoredFiles };
            }
        }

        return {
            status: "ok",
            files,
            sensitiveFiles,
            fernignoredFiles,
            patchContent,
            contentHash: this.computeContentHash(patchContent),
        };
    }

    /**
     * Compute content hash for deduplication.
     *
     * Produces a format-agnostic hash so both `git format-patch` output
     * (email-wrapped) and plain `git diff` output hash to the same value
     * when the underlying diff hunks are identical.
     *
     * Normalization:
     *   1. Strip email wrapper: everything before the first `diff --git` line
     *      (From:, Subject:, Date:, diffstat, blank separators).
     *   2. Strip `index` lines (blob SHA pairs change across rebases).
     *   3. Strip trailing git version marker (`-- \n<version>`).
     *
     * This ensures content hashes match across the no-patches and normal-
     * regeneration flows, which store formatPatch and git-diff formats
     * respectively.
     */
    computeContentHash(patchContent: string): string {
        const lines = patchContent.split("\n");

        // Strip email wrapper: everything before the first 'diff --git'
        const diffStart = lines.findIndex((l) => l.startsWith("diff --git "));
        const relevantLines = diffStart > 0 ? lines.slice(diffStart) : lines;

        const normalized = relevantLines
            .filter((line) => !line.startsWith("index "))
            .join("\n")
            // Strip trailing git version marker ("-- \n2.x.x")
            .replace(/\n-- \n[\d]+\.[\d]+[^\n]*\s*$/, "");

        return `sha256:${createHash("sha256").update(normalized).digest("hex")}`;
    }

    /**
     * Drop patches whose files were transiently created and then deleted
     * within the current linear detection window, and are absent from
     * HEAD at the end of the window.
     *
     * Rationale: each commit becomes its own patch, so a file that was
     * briefly added and later removed survives as a create+delete pair whose
     * cumulative diff is empty. We drop such patches before they reach the
     * lockfile.
     *
     * A file is "transient" when:
     *   1. some patch in the window sets `new file mode` for it (a creation), AND
     *   2. some patch in the window sets `deleted file mode` for it (a deletion), AND
     *   3. it is absent from HEAD's tree.
     *
     * The HEAD guard (#3) prevents false positives when a file is deleted
     * and then recreated with different content — the cumulative diff of
     * such a pair is non-empty and must be preserved.
     *
     * This only drops patches whose `files` are a subset of the transient
     * set. A partial-transient patch (one that touches a transient file
     * alongside a persistent one) is left unmodified; surgical stripping of
     * single files from a multi-file patch would require a follow-up that
     * regenerates the patch content via `git format-patch -- <files>`. No
     * current failing test exercises this, and leaving the patch intact
     * preserves today's behaviour. Revisit if a future adversarial test
     * demands per-file stripping.
     */
    private async collapseNetZeroFiles(patches: StoredPatch[]): Promise<StoredPatch[]> {
        if (patches.length === 0) return patches;

        type FileState = { created: boolean; deleted: boolean };
        const stateByFile = new Map<string, FileState>();

        for (const patch of patches) {
            for (const header of parsePatchFileHeaders(patch.patch_content)) {
                if (!header.isCreate && !header.isDelete) continue;
                const prior = stateByFile.get(header.path) ?? { created: false, deleted: false };
                if (header.isCreate) prior.created = true;
                if (header.isDelete) prior.deleted = true;
                stateByFile.set(header.path, prior);
            }
        }

        // No file has both a create and a delete event in the window → nothing to collapse.
        let anyCandidate = false;
        for (const state of stateByFile.values()) {
            if (state.created && state.deleted) { anyCandidate = true; break; }
        }
        if (!anyCandidate) return patches;

        const headFiles = await this.listHeadFiles();

        const transient = new Set<string>();
        for (const [file, state] of stateByFile) {
            if (state.created && state.deleted && !headFiles.has(file)) {
                transient.add(file);
            }
        }

        if (transient.size === 0) return patches;

        return patches.filter((patch) => !patch.files.every((f) => transient.has(f)));
    }

    /**
     * List every tracked path at HEAD (one `git ls-tree -r` invocation).
     * Returns an empty Set if HEAD is unborn or the invocation fails — the
     * caller then treats every candidate as "not in HEAD" and may collapse
     * more aggressively. On typical SDK repos this is a single sub-10ms call.
     */
    private async listHeadFiles(): Promise<Set<string>> {
        try {
            const out = await this.git.exec(["ls-tree", "-r", "--name-only", "HEAD"]);
            return new Set(out.split("\n").map((l) => l.trim()).filter(Boolean));
        } catch {
            return new Set();
        }
    }

    /**
     * Detect patches via tree diff for non-linear history. Returns a composite patch.
     * Revert reconciliation is skipped here because tree-diff produces a single composite
     * patch from the aggregate diff — individual revert commits are not distinguishable.
     */
    private async detectPatchesViaTreeDiff(
        lastGen: GenerationRecord,
        commitKnownMissing: boolean
    ): Promise<DetectionResult> {
        // Use commit_sha if reachable, fall back to tree_hash for unreachable commits.
        // git diff accepts both commit and tree objects as arguments.
        const diffBase = await this.resolveDiffBase(lastGen, commitKnownMissing);
        if (!diffBase) {
            // Both commit and tree are gone (aggressive GC / shallow clone with
            // the generation outside the window). Fall back to scanning all
            // commits from HEAD and filtering against known lockfile patches.
            //
            // This transition was previously SILENT — the production incident
            // path: the commit scan cannot see customizations older than the
            // clone window, so a regen PR can wipe customer code with zero
            // signal. Always loud, regardless of how many patches are tracked.
            this.warnings.push(
                `Neither the previous generation's commit (${lastGen.commit_sha.slice(0, 7)}) ` +
                    `nor its recorded tree was reachable during this run. Falling back to a ` +
                    `bounded commit scan, which cannot see customizations older than the clone ` +
                    `window. Widen the clone window (e.g. \`git fetch --unshallow\`) and re-run ` +
                    `replay to verify customizations were carried forward.`
            );
            this.degradedReasons.push({
                code: "generation-tree-unreachable",
                message:
                    `The previous generation (${lastGen.commit_sha.slice(0, 7)}) was not ` +
                    `reachable during this run — neither its commit nor its recorded tree. ` +
                    `Customizations may not have been carried forward — review the result ` +
                    `before merging, or widen the clone window and re-run replay.`,
            });
            return this.detectPatchesViaCommitScan();
        }

        // Data-integrity guard: composite patches must anchor on a recorded
        // [fern-generated] commit. When the original generation commit is
        // gone and diffBase resolves to some pre-Fern commit, emitting a
        // composite patch with that diffBase as base_generation would
        // orphan the patch — `resolveBaseGeneration` only finds SHAs in
        // `lock.generations`. Re-anchor on the lockfile's recorded
        // generation instead so downstream remains consistent.
        const lockForGuard = this.lockManager.read();
        const knownGenerations = new Set(lockForGuard.generations.map((g) => g.commit_sha));
        let resolvedBaseGeneration = commitKnownMissing ? diffBase : lastGen.commit_sha;
        if (commitKnownMissing && !knownGenerations.has(diffBase)) {
            const fallback = lockForGuard.current_generation
                ?? lockForGuard.generations[lockForGuard.generations.length - 1]?.commit_sha;
            if (!fallback) {
                this.warnings.push(
                    `Composite-patch fallback skipped: diff base ${diffBase.slice(0, 7)} ` +
                    `is not a recorded generation and the lockfile has no recorded generations ` +
                    `to anchor on. Customer customizations on disk are preserved; tracked ` +
                    `patches will resume on the next regeneration.`
                );
                return { patches: [], revertedPatchIds: [] };
            }
            resolvedBaseGeneration = fallback;
        }

        // Get changed files first, then filter before computing the diff
        const filesOutput = await this.git.exec(["diff", "--name-only", diffBase, "HEAD"]);
        const { files, sensitiveFiles, fernignoredFiles } = filterInfrastructureAndSensitiveFiles(
            filesOutput,
            this.fernignorePatterns,
        );

        if (sensitiveFiles.length > 0) {
            this.warnings.push(
                `Sensitive file(s) excluded from composite patch: ` +
                `${sensitiveFiles.join(", ")}. These files will not be tracked by replay.`
            );
        }

        if (fernignoredFiles.length > 0) {
            this.warnings.push(
                `.fernignore-protected file(s) excluded from composite patch: ` +
                `${fernignoredFiles.join(", ")}. These files are managed by .fernignore, not Replay.`
            );
        }

        if (files.length === 0) return { patches: [], revertedPatchIds: [] };

        // Compute diff only for the filtered files
        const diff = await this.git.exec(["diff", diffBase, "HEAD", "--", ...files]);
        if (!diff.trim()) return { patches: [], revertedPatchIds: [] };

        const contentHash = this.computeContentHash(diff);

        // Dedup against existing patches and forgotten hashes — if an existing patch
        // already captures this content, or it was explicitly forgotten, skip it.
        const lock = this.lockManager.read();
        if (lock.patches.some((p) => p.content_hash === contentHash) || (lock.forgotten_hashes ?? []).includes(contentHash)) {
            return { patches: [], revertedPatchIds: [] };
        }

        const headSha = (await this.git.exec(["rev-parse", "HEAD"])).trim();

        // Snapshot THEIRS from HEAD (the diff is diffBase → HEAD).
        const theirsSnapshot = await capturePatchSnapshot(this.git, files, "HEAD");

        const compositePatch: StoredPatch = {
            id: `patch-composite-${headSha.slice(0, 8)}`,
            content_hash: contentHash,
            original_commit: headSha,
            original_message: "Customer customizations (composite)",
            original_author: "composite",
            // Anchor `base_generation` on a SHA the applicator's
            // `resolveBaseGeneration` can find — always a member of
            // `lock.generations`. When `diffBase` is a recorded
            // generation, use it directly; otherwise the guard above
            // has already mapped it onto `current_generation`.
            base_generation: resolvedBaseGeneration,
            files,
            patch_content: diff,
            ...(Object.keys(theirsSnapshot).length > 0 ? { theirs_snapshot: theirsSnapshot } : {}),
            ...(this.isCreationOnlyPatch(diff) ? { user_owned: true } : {})
        };

        return { patches: [compositePatch], revertedPatchIds: [] };
    }

    /**
     * Last-resort detection when both generation commit and tree are unreachable.
     * Scans all commits from HEAD, filters against known lockfile patches, and
     * skips creation-only commits (squashed history after force push).
     *
     * Detected patches use the commit's parent as base_generation so the applicator
     * can find base file content from the parent's tree (which IS reachable).
     */
    private async detectPatchesViaCommitScan(): Promise<DetectionResult> {
        const lock = this.lockManager.read();
        const knownGenerations = new Set(lock.generations.map((g) => g.commit_sha));
        // Anchor for emitted patches when the parent commit isn't itself a
        // recorded generation: prefer current_generation, fall back to the
        // last generation in the list. This keeps `base_generation` pointing
        // at a SHA that the applicator's `resolveBaseGeneration` can find,
        // even when the actual git history is rewritten or shallow.
        const fallbackAnchor =
            lock.current_generation ?? lock.generations[lock.generations.length - 1]?.commit_sha;
        const log = await this.git.exec([
            "log",
            "--max-count=200",
            "--format=%H%x00%an%x00%ae%x00%s",
            "HEAD",
            "--",
            this.sdkOutputDir
        ]);

        if (!log.trim()) {
            return { patches: [], revertedPatchIds: [] };
        }

        const commits = this.parseGitLog(log);
        const newPatches: StoredPatch[] = [];
        const forgottenHashes = new Set(lock.forgotten_hashes ?? []);
        const existingHashes = new Set(lock.patches.map((p) => p.content_hash));
        const existingCommits = new Set(lock.patches.map((p) => p.original_commit));

        for (const commit of commits) {
            if (isGenerationCommit(commit)) {
                continue;
            }

            const parents = await this.git.getCommitParents(commit.sha);
            if (parents.length > 1) {
                continue;
            }

            if (existingCommits.has(commit.sha)) {
                continue;
            }

            let patchContent: string;
            try {
                patchContent = await this.git.formatPatch(commit.sha);
            } catch {
                continue;
            }

            let contentHash = this.computeContentHash(patchContent);
            if (existingHashes.has(contentHash) || forgottenHashes.has(contentHash)) {
                continue;
            }

            // Skip creation-only commits (all diffs are new-file additions).
            // After force push, squashed commits create all files from scratch — these
            // aren't user customizations, they're history-rewriting artifacts.
            if (this.isCreationOnlyPatch(patchContent)) {
                continue;
            }

            const filesOutput = await this.git.exec(["diff-tree", "--no-commit-id", "--name-only", "-r", commit.sha]);
            const { files, sensitiveFiles, fernignoredFiles, infrastructureFiles } =
                filterInfrastructureAndSensitiveFiles(filesOutput, this.fernignorePatterns);

            if (sensitiveFiles.length > 0) {
                this.warnings.push(
                    `Sensitive file(s) excluded from patch for commit ${commit.sha.slice(0, 7)}: ` +
                    `${sensitiveFiles.join(", ")}. These files will not be tracked by replay.`
                );
            }

            if (fernignoredFiles.length > 0) {
                this.warnings.push(
                    `.fernignore-protected file(s) excluded from patch for commit ${commit.sha.slice(0, 7)}: ` +
                    `${fernignoredFiles.join(", ")}. These files are managed by .fernignore, not Replay.`
                );
            }

            // Regenerate patch content scoped to surviving files when any files
            // were stripped (infrastructure, sensitive, or fernignored).
            const wasFiltered =
                sensitiveFiles.length > 0 || fernignoredFiles.length > 0 || infrastructureFiles.length > 0;
            if (wasFiltered && files.length > 0) {
                if (parents.length === 0) {
                    continue;
                }
                try {
                    patchContent = await this.git.exec(["diff", parents[0]!, commit.sha, "--", ...files]);
                } catch {
                    continue;
                }
                if (!patchContent.trim()) continue;
                contentHash = this.computeContentHash(patchContent);
            }

            if (files.length === 0) {
                continue;
            }

            // Use the parent commit as base_generation when it's a recorded
            // generation, so the applicator can find base file content from
            // a known anchor. Otherwise fall back to the lockfile's most
            // recent recorded generation — patches with a parent SHA outside
            // `lock.generations` would otherwise create orphans the
            // applicator's `resolveBaseGeneration` cannot resolve, cascading
            // into spurious unresolved-conflict classifications.
            // Root commits (no parents) can't use this strategy — skip them.
            if (parents.length === 0) {
                continue;
            }
            const parentSha = parents[0]!;
            let baseGeneration: string;
            if (knownGenerations.has(parentSha)) {
                baseGeneration = parentSha;
            } else if (fallbackAnchor) {
                baseGeneration = fallbackAnchor;
            } else {
                // No recorded generations at all — nothing safe to anchor on.
                this.warnings.push(
                    `Skipping commit ${commit.sha.slice(0, 7)} (${commit.message.split("\n")[0]}): ` +
                    `no recorded generation in the lockfile to anchor on. The customer's ` +
                    `state on disk is preserved; tracked patches will resume on the next regeneration.`
                );
                continue;
            }

            // user_owned not tagged here — see known-divergences.md §3.
            const theirsSnapshot = await capturePatchSnapshot(this.git, files, commit.sha);
            newPatches.push({
                id: `patch-${commit.sha.slice(0, 8)}`,
                content_hash: contentHash,
                original_commit: commit.sha,
                original_message: commit.message,
                original_author: `${commit.authorName} <${commit.authorEmail}>`,
                base_generation: baseGeneration,
                files,
                patch_content: patchContent,
                ...(Object.keys(theirsSnapshot).length > 0 ? { theirs_snapshot: theirsSnapshot } : {})
            });
        }

        newPatches.reverse();
        return { patches: newPatches, revertedPatchIds: [] };
    }

    /**
     * Check if a format-patch consists entirely of new-file creations.
     * Used to identify squashed commits after force push, which create all files
     * from scratch (--- /dev/null) rather than modifying existing files.
     */
    private isCreationOnlyPatch(patchContent: string): boolean {
        const diffOldHeaders = patchContent.split("\n").filter((l) => l.startsWith("--- "));
        if (diffOldHeaders.length === 0) {
            return false;
        }
        return diffOldHeaders.every((l) => l === "--- /dev/null");
    }

    /**
     * Resolve the best available diff base for a generation record.
     * Prefers commit_sha, falls back to tree_hash for unreachable commits.
     * When commitKnownMissing is true, skips the redundant commitExists check.
     */
    private async resolveDiffBase(gen: GenerationRecord, commitKnownMissing: boolean): Promise<string | null> {
        if (!commitKnownMissing && await this.git.commitExists(gen.commit_sha)) {
            return gen.commit_sha;
        }
        if (await this.git.treeExists(gen.tree_hash)) {
            return gen.tree_hash;
        }
        return null;
    }

    private parseGitLog(log: string): CommitInfo[] {
        return log
            .trim()
            .split("\n")
            .map((line) => {
                const [sha, authorName, authorEmail, message] = line.split("\0");
                return { sha, authorName, authorEmail, message };
            });
    }

    private getLastGeneration(lock: GenerationLock) {
        return lock.generations.find((g) => g.commit_sha === lock.current_generation);
    }

    /**
     * Build a transient `GenerationRecord` from a SHA derived by walking git
     * history. Only `commit_sha` (and `tree_hash` when needed downstream) is
     * load-bearing here; the rest are filled with defensible placeholders.
     * This record is consumed by `detectPatchesInRange` and is never persisted.
     */
    private async synthesizeGenerationRecord(sha: string): Promise<GenerationRecord> {
        let tree_hash = "";
        try {
            tree_hash = await this.git.getTreeHash(sha);
        } catch {
            // Reachable by construction (we just walked git log to find it),
            // but defensively swallow — downstream uses commit_sha, not tree.
        }
        return {
            commit_sha: sha,
            tree_hash,
            timestamp: new Date(0).toISOString(),
            cli_version: "",
            generator_versions: {},
        };
    }
}
